import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  AlertTriangle,
  ArrowRight,
  Phone,
  Search,
  Heart,
  Brain,
  Shield,
  Activity,
  DollarSign,
  AlertCircle,
  TrendingDown
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const LudopatiaPage = () => {
  const gamblingTypes = [
    {
      type: "Casinos y Máquinas Tragamonedas",
      description: "Las máquinas están diseñadas para ser altamente adictivas con estímulos visuales y sonoros.",
      risk: "Muy alto"
    },
    {
      type: "Apuestas Deportivas Online",
      description: "Fácil acceso 24/7 desde el celular. Las apuestas en vivo aumentan la impulsividad.",
      risk: "Alto"
    },
    {
      type: "Poker y Juegos de Cartas",
      description: "La ilusión de habilidad hace que sea fácil justificar continuar jugando.",
      risk: "Alto"
    },
    {
      type: "Lotería y Raspes",
      description: "Parecen inofensivos pero pueden escalar a formas más intensas de juego.",
      risk: "Moderado"
    }
  ];

  const warningSigns = [
    { title: "Apuestas crecientes", description: "Necesidad de apostar cantidades cada vez mayores para sentir emoción" },
    { title: "Perseguir pérdidas", description: "Seguir jugando para intentar recuperar el dinero perdido" },
    { title: "Mentir sobre el juego", description: "Ocultar a familiares cuánto dinero y tiempo dedicas al juego" },
    { title: "Deudas y problemas financieros", description: "Pedir prestado, vender cosas o robar para financiar el juego" },
    { title: "Intentos fallidos de parar", description: "Has intentado dejar o reducir el juego sin éxito" },
    { title: "Descuidar responsabilidades", description: "El juego afecta tu trabajo, estudios o relaciones familiares" }
  ];

  const consequences = [
    { area: "Financieras", effects: ["Deudas graves", "Pérdida de ahorros", "Bancarrota", "Pérdida de vivienda"] },
    { area: "Familiares", effects: ["Divorcios", "Pérdida de confianza", "Conflictos constantes", "Alejamiento de hijos"] },
    { area: "Laborales", effects: ["Bajo rendimiento", "Despidos", "Robos en el trabajo", "Pérdida de oportunidades"] },
    { area: "Legales", effects: ["Fraudes", "Estafas", "Robos", "Procesos judiciales"] }
  ];

  const treatmentOptions = [
    {
      title: "Terapia Cognitivo-Conductual",
      description: "Muy efectiva para ludopatía. Trabaja los pensamientos distorsionados sobre el juego y las probabilidades.",
      icon: Brain
    },
    {
      title: "Grupos de Apoyo (GA)",
      description: "Jugadores Anónimos ofrece apoyo continuo basado en los 12 pasos. Gratuito y confidencial.",
      icon: Shield
    },
    {
      title: "Control de Acceso",
      description: "Autoexclusión de casinos, bloqueo de sitios de apuestas, control del dinero por terceros.",
      icon: Activity
    },
    {
      title: "Tratamiento de Condiciones Asociadas",
      description: "La ludopatía suele coexistir con depresión, ansiedad u otras adicciones que deben tratarse.",
      icon: Heart
    }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    "name": "Ludopatía",
    "alternateName": ["Adicción al juego", "Juego patológico", "Trastorno del juego"],
    "description": "La ludopatía es un trastorno del control de impulsos caracterizado por la necesidad incontrolable de jugar a pesar de las consecuencias negativas."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Adicciones", "item": "https://sinadicciones.org/adicciones" },
      { "@type": "ListItem", "position": 3, "name": "Ludopatía", "item": "https://sinadicciones.org/adicciones/ludopatia" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Ludopatía: Adicción al Juego - Síntomas y Tratamiento | Sin Adicciones</title>
        <meta name="description" content="Todo sobre la ludopatía o adicción al juego en Chile. Señales de alerta, consecuencias financieras y familiares, y opciones de tratamiento efectivas." />
        <link rel="canonical" href="https://sinadicciones.org/adicciones/ludopatia" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones/ludopatia" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/adicciones" className="hover:text-emerald-400 transition-colors">
              Adicciones
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Ludopatía</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-5xl">🎰</span>
              <div className="px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full">
                <span className="text-purple-400 font-medium">Adicción silenciosa</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Ludopatía:{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                Adicción al Juego
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              La ludopatía destruye silenciosamente finanzas, familias y vidas. 
              No involucra sustancias, pero el cerebro responde de forma similar a las drogas. 
              La buena noticia: es tratable.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/herramientas/pgsi"
                className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Activity className="w-5 h-5" />
                Test de Evaluación (PGSI)
              </Link>
              <a 
                href="tel:1412"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Fono Drogas: 1412
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Gambling */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Tipos de Juego con Mayor Riesgo Adictivo
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              No todos los juegos tienen el mismo potencial adictivo. Conoce cuáles representan mayor riesgo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {gamblingTypes.map((type, index) => (
              <div 
                key={index}
                className={`p-6 rounded-2xl border ${
                  type.risk === 'Muy alto' ? 'bg-red-500/10 border-red-500/30' :
                  type.risk === 'Alto' ? 'bg-orange-500/10 border-orange-500/30' :
                  'bg-yellow-500/10 border-yellow-500/30'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-white">{type.type}</h3>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    type.risk === 'Muy alto' ? 'bg-red-500/20 text-red-400' :
                    type.risk === 'Alto' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    Riesgo {type.risk}
                  </span>
                </div>
                <p className="text-slate-300">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Señales de Ludopatía
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              ¿Reconoces estas señales en ti o en alguien cercano?
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {warningSigns.map((sign, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-purple-500/20"
              >
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{sign.title}</h3>
                    <p className="text-slate-400 text-sm">{sign.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link 
              to="/herramientas/pgsi"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl transition-all"
            >
              Evaluar mi situación con el Test PGSI
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Consequences */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Consecuencias de la Ludopatía
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              El juego patológico afecta todas las áreas de la vida. Estas son las consecuencias más comunes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {consequences.map((item, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50"
              >
                <div className="flex items-center gap-2 mb-4">
                  <TrendingDown className="w-6 h-6 text-red-400" />
                  <h3 className="text-lg font-bold text-white">{item.area}</h3>
                </div>
                <ul className="space-y-2">
                  {item.effects.map((effect, idx) => (
                    <li key={idx} className="text-slate-400 text-sm flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                      {effect}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Opciones de Tratamiento
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              La ludopatía responde bien al tratamiento. Estas son las opciones más efectivas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {treatmentOptions.map((option, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <option.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{option.title}</h3>
                    <p className="text-slate-400">{option.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-purple-900/30 to-slate-800/50 rounded-3xl p-8 md:p-12 border border-purple-500/20">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
              Ludopatía en Cifras
            </h2>
            
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-purple-400 mb-2">2.4%</p>
                <p className="text-slate-400">de la población tiene problemas con el juego</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-purple-400 mb-2">40%</p>
                <p className="text-slate-400">de los ludópatas tienen deudas graves</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-purple-400 mb-2">90%</p>
                <p className="text-slate-400">mejoran con tratamiento adecuado</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12 text-center">
            <Heart className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              El Juego No Tiene que Controlar Tu Vida
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Miles de personas han superado la ludopatía. Con ayuda profesional y apoyo adecuado, 
              tú también puedes recuperar el control.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
              <Link 
                to="/profesionales"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700"
              >
                Buscar Especialista
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Contenido Relacionado</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Adicciones Conductuales", path: "/adicciones/conductuales", icon: "📱" },
              { title: "Terapia Cognitivo-Conductual", path: "/tratamiento-cognitivo-conductual", icon: "🧠" },
              { title: "Tratamiento Ambulatorio", path: "/tratamiento-ambulatorio", icon: "🏥" },
              { title: "Test PGSI", path: "/herramientas/pgsi", icon: "📋" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-emerald-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LudopatiaPage;
